// src/pages/SuperAdmin/SuperAdminDashboard.jsx
import React, { useState, useEffect } from 'react';
import {
    Container, Row, Col, Card, Button, Table, Badge, Modal, Form,
    Spinner, Alert, Tabs, Tab
} from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import axios from '../../api/axios';

const SuperAdminDashboard = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [activeTab, setActiveTab] = useState('dashboard');

    // États pour les utilisateurs
    const [users, setUsers] = useState([]);
    const [showUserModal, setShowUserModal] = useState(false);
    const [userForm, setUserForm] = useState({
        email: '', first_name: '', last_name: '', phone: '', role: 'admin', password: ''
    });

    // États pour WhatsApp
    const [whatsappConfigs, setWhatsappConfigs] = useState([]);
    const [showWhatsappModal, setShowWhatsappModal] = useState(false);
    const [whatsappForm, setWhatsappForm] = useState({ phone_number: '', is_default: false });

    // États pour CMS
    const [pages, setPages] = useState([]);
    const [showPageModal, setShowPageModal] = useState(false);
    const [editingPage, setEditingPage] = useState(null);
    const [pageForm, setPageForm] = useState({ title: '', content: '', slug: '', is_published: true });

    // Statistiques
    const [stats, setStats] = useState({
        total_users: 0, total_admins: 0, total_operators: 0, total_clients: 0
    });

    useEffect(() => {
        fetchAllData();
    }, []);

    const fetchAllData = async () => {
        try {
            setLoading(true);
            await Promise.all([
                fetchUsers(),
                fetchWhatsappConfigs(),
                fetchPages()
            ]);
        } catch (err) {
            console.error('Erreur chargement:', err);
            setError('Erreur lors du chargement des données');
        } finally {
            setLoading(false);
        }
    };

    const fetchUsers = async () => {
        try {
            const response = await axios.get('/auth/users/');
            const usersData = Array.isArray(response.data) ? response.data : [];
            setUsers(usersData);

            setStats({
                total_users: usersData.length,
                total_admins: usersData.filter(u => u.role === 'admin').length,
                total_operators: usersData.filter(u => u.role === 'operator').length,
                total_clients: usersData.filter(u => u.role === 'client').length
            });
        } catch (err) {
            console.error('Erreur chargement utilisateurs:', err);
            setUsers([]);
        }
    };

    const fetchWhatsappConfigs = async () => {
        try {
            // Utiliser l'API publique pour récupérer les numéros actifs
            const response = await axios.get('/whatsapp/public/');

            // Gérer le format paginé de Django REST Framework
            let configsData = [];
            if (response.data && response.data.results) {
                configsData = response.data.results;
            } else if (Array.isArray(response.data)) {
                configsData = response.data;
            }

            setWhatsappConfigs(configsData);
        } catch (err) {
            console.error('Erreur chargement WhatsApp:', err);
            setWhatsappConfigs([]);
        }
    };

    const fetchPages = async () => {
        try {
            const response = await axios.get('/cms/pages/');
            const pagesData = Array.isArray(response.data) ? response.data : [];
            setPages(pagesData);
        } catch (err) {
            console.error('Erreur chargement pages:', err);
            setPages([]);
        }
    };

    // Gestion des utilisateurs
    const handleUserSubmit = async (e) => {
        e.preventDefault();
        try {
            await axios.post('/auth/users/', userForm);
            toast.success('Utilisateur créé avec succès');
            setShowUserModal(false);
            setUserForm({ email: '', first_name: '', last_name: '', phone: '', role: 'admin', password: '' });
            fetchUsers();
        } catch (err) {
            toast.error(err.response?.data?.error || 'Erreur lors de la création');
        }
    };

    const deleteUser = async (user) => {
        if (window.confirm(`Supprimer ${user.email} ?`)) {
            try {
                await axios.delete(`/auth/users/${user.id}/`);
                toast.success('Utilisateur supprimé');
                fetchUsers();
            } catch (err) {
                toast.error('Erreur lors de la suppression');
            }
        }
    };

    // Gestion WhatsApp
    const handleWhatsappSubmit = async (e) => {
        e.preventDefault();
        try {
            if (whatsappForm.id) {
                await axios.put(`/whatsapp/config/${whatsappForm.id}/`, whatsappForm);
                toast.success('Numéro modifié');
            } else {
                await axios.post('/whatsapp/config/', whatsappForm);
                toast.success('Numéro ajouté');
            }
            setShowWhatsappModal(false);
            setWhatsappForm({ phone_number: '', is_default: false });
            fetchWhatsappConfigs();
        } catch (err) {
            console.error('Erreur:', err);
            toast.error(err.response?.data?.message || 'Erreur lors de l\'opération');
        }
    };

    const deleteWhatsappConfig = async (id) => {
        if (window.confirm('Supprimer ce numéro ?')) {
            try {
                await axios.delete(`/whatsapp/config/${id}/`);
                toast.success('Numéro supprimé');
                fetchWhatsappConfigs();
            } catch (err) {
                console.error('Erreur:', err);
                toast.error('Erreur lors de la suppression');
            }
        }
    };

    // Gestion CMS
    const handlePageSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingPage) {
                await axios.put(`/cms/pages/${editingPage.id}/`, pageForm);
                toast.success('Page modifiée');
            } else {
                await axios.post('/cms/pages/', pageForm);
                toast.success('Page créée');
            }
            setShowPageModal(false);
            setEditingPage(null);
            setPageForm({ title: '', content: '', slug: '', is_published: true });
            fetchPages();
        } catch (err) {
            toast.error('Erreur lors de l\'opération');
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            return new Date(dateString).toLocaleDateString('fr-FR', {
                day: '2-digit', month: '2-digit', year: 'numeric'
            });
        } catch {
            return '-';
        }
    };

    if (loading) {
        return (
            <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                <Spinner animation="border" style={{ color: '#C62828' }} />
            </Container>
        );
    }

    return (
        <Container className="py-4">
            {error && <Alert variant="danger">{error}</Alert>}

            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2 className="fw-bold" style={{ color: '#C62828' }}>
                    👑 SuperAdmin - Administration CAC
                </h2>
                <Button
                    variant="outline-danger"
                    as={Link}
                    to="/superadmin/StoreManagement"
                    className="d-flex align-items-center gap-2"
                >
                    🛍️ Gérer le Store
                </Button>
            </div>

            <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-4" fill>

                {/* Dashboard */}
                <Tab eventKey="dashboard" title="📊 Dashboard">
                    <Row className="mb-4">
                        <Col md={4} className="mb-3">
                            <Card className="border-0 shadow-sm text-center bg-info bg-opacity-10">
                                <Card.Body>
                                    <div className="display-6 fw-bold text-info">{stats.total_users}</div>
                                    <div className="text-muted">Utilisateurs</div>
                                    <small>Admins: {stats.total_admins} | Ops: {stats.total_operators} | Clients: {stats.total_clients}</small>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={4} className="mb-3">
                            <Card className="border-0 shadow-sm text-center bg-success bg-opacity-10">
                                <Card.Body>
                                    <div className="display-6 fw-bold text-success">{whatsappConfigs.length}</div>
                                    <div className="text-muted">Numéros WhatsApp</div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={4} className="mb-3">
                            <Card className="border-0 shadow-sm text-center bg-warning bg-opacity-10">
                                <Card.Body>
                                    <div className="display-6 fw-bold text-warning">{pages.length}</div>
                                    <div className="text-muted">Pages CMS</div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Tab>

                {/* Utilisateurs */}
                <Tab eventKey="users" title="👥 Utilisateurs">
                    <Card className="border-0 shadow-lg rounded-4">
                        <Card.Header className="bg-white border-0 pt-4 d-flex justify-content-between">
                            <h4 className="fw-bold mb-0">Gestion des utilisateurs</h4>
                            <Button variant="danger" onClick={() => setShowUserModal(true)}>+ Nouvel utilisateur</Button>
                        </Card.Header>
                        <Card.Body>
                            <div className="table-responsive">
                                <Table hover>
                                    <thead className="bg-light">
                                        <tr>
                                            <th>Email</th>
                                            <th>Nom</th>
                                            <th>Téléphone</th>
                                            <th>Rôle</th>
                                            <th>Date création</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {users.map(u => (
                                            <tr key={u.id}>
                                                <td>{u.email}</td>
                                                <td>{u.first_name} {u.last_name}</td>
                                                <td>{u.phone || '-'}</td>
                                                <td>
                                                    <Badge bg={u.role === 'superadmin' ? 'danger' : u.role === 'admin' ? 'warning' : u.role === 'operator' ? 'info' : 'secondary'}>
                                                        {u.role}
                                                    </Badge>
                                                </td>
                                                <td>{formatDate(u.date_joined)}</td>
                                                <td>
                                                    {u.role !== 'superadmin' && (
                                                        <Button size="sm" variant="outline-danger" onClick={() => deleteUser(u)}>
                                                            Supprimer
                                                        </Button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                    </Card>
                </Tab>

                {/* WhatsApp */}
                <Tab eventKey="whatsapp" title="📱 WhatsApp">
                    <Card className="border-0 shadow-lg rounded-4">
                        <Card.Header className="bg-white border-0 pt-4 d-flex justify-content-between">
                            <h4 className="fw-bold mb-0">Configuration WhatsApp</h4>
                            <Button variant="danger" onClick={() => {
                                setWhatsappForm({ phone_number: '', is_default: false });
                                setShowWhatsappModal(true);
                            }}>
                                + Ajouter un numéro
                            </Button>
                        </Card.Header>
                        <Card.Body>
                            {whatsappConfigs.length === 0 ? (
                                <div className="text-center py-5 text-muted">
                                    <p>Aucun numéro WhatsApp configuré</p>
                                    <small>Cliquez sur "+ Ajouter un numéro" pour commencer</small>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <Table hover>
                                        <thead className="bg-light">
                                            <tr>
                                                <th>Numéro</th>
                                                <th>Statut</th>
                                                <th>Défaut</th>
                                                <th>Date ajout</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {whatsappConfigs.map(w => (
                                                <tr key={w.id}>
                                                    <td className="font-monospace">{w.phone_number}</td>
                                                    <td>
                                                        {w.is_active ?
                                                            <Badge bg="success">Actif</Badge> :
                                                            <Badge bg="secondary">Inactif</Badge>
                                                        }
                                                    </td>
                                                    <td>
                                                        {w.is_default ?
                                                            <Badge bg="warning" text="dark">⭐ Défaut</Badge> :
                                                            <span className="text-muted">-</span>
                                                        }
                                                    </td>
                                                    <td>{formatDate(w.created_at)}</td>
                                                    <td>
                                                        <Button
                                                            size="sm"
                                                            variant="outline-danger"
                                                            onClick={() => deleteWhatsappConfig(w.id)}
                                                        >
                                                            Supprimer
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Tab>

                {/* CMS */}
                <Tab eventKey="cms" title="📄 CMS">
                    <Card className="border-0 shadow-lg rounded-4">
                        <Card.Header className="bg-white border-0 pt-4 d-flex justify-content-between">
                            <h4 className="fw-bold mb-0">Gestion des pages</h4>
                            <Button variant="danger" onClick={() => {
                                setEditingPage(null);
                                setPageForm({ title: '', content: '', slug: '', is_published: true });
                                setShowPageModal(true);
                            }}>
                                + Nouvelle page
                            </Button>
                        </Card.Header>
                        <Card.Body>
                            {pages.length === 0 ? (
                                <div className="text-center py-5 text-muted">
                                    <p>Aucune page CMS créée</p>
                                    <small>Cliquez sur "+ Nouvelle page" pour commencer</small>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <Table hover>
                                        <thead className="bg-light">
                                            <tr>
                                                <th>Titre</th>
                                                <th>Slug</th>
                                                <th>Statut</th>
                                                <th>Dernière modif</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {pages.map(p => (
                                                <tr key={p.id}>
                                                    <td>{p.title}</td>
                                                    <td className="font-monospace">{p.slug}</td>
                                                    <td>
                                                        {p.is_published ?
                                                            <Badge bg="success">Publiée</Badge> :
                                                            <Badge bg="secondary">Brouillon</Badge>
                                                        }
                                                    </td>
                                                    <td>{formatDate(p.updated_at)}</td>
                                                    <td>
                                                        <Button
                                                            size="sm"
                                                            variant="outline-primary"
                                                            className="me-2"
                                                            onClick={() => {
                                                                setEditingPage(p);
                                                                setPageForm(p);
                                                                setShowPageModal(true);
                                                            }}
                                                        >
                                                            Modifier
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Tab>
            </Tabs>

            {/* Modal Utilisateur */}
            <Modal show={showUserModal} onHide={() => setShowUserModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Créer un utilisateur</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleUserSubmit}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Email</Form.Label>
                            <Form.Control
                                type="email"
                                value={userForm.email}
                                onChange={e => setUserForm({ ...userForm, email: e.target.value })}
                                required
                            />
                        </Form.Group>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Prénom</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={userForm.first_name}
                                        onChange={e => setUserForm({ ...userForm, first_name: e.target.value })}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Nom</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={userForm.last_name}
                                        onChange={e => setUserForm({ ...userForm, last_name: e.target.value })}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Form.Group className="mb-3">
                            <Form.Label>Téléphone</Form.Label>
                            <Form.Control
                                type="text"
                                value={userForm.phone}
                                onChange={e => setUserForm({ ...userForm, phone: e.target.value })}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Rôle</Form.Label>
                            <Form.Select
                                value={userForm.role}
                                onChange={e => setUserForm({ ...userForm, role: e.target.value })}
                            >
                                <option value="admin">Administrateur</option>
                                <option value="operator">Opérateur</option>
                            </Form.Select>
                        </Form.Group>
                        <Form.Group>
                            <Form.Label>Mot de passe</Form.Label>
                            <Form.Control
                                type="text"
                                value={userForm.password}
                                onChange={e => setUserForm({ ...userForm, password: e.target.value })}
                                placeholder="Laissez vide pour génération auto"
                            />
                            <Form.Text className="text-muted">
                                Un email sera envoyé automatiquement avec les identifiants
                            </Form.Text>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowUserModal(false)}>Annuler</Button>
                        <Button type="submit" variant="danger">Créer</Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Modal WhatsApp */}
            <Modal show={showWhatsappModal} onHide={() => setShowWhatsappModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Ajouter un numéro WhatsApp</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleWhatsappSubmit}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Numéro de téléphone</Form.Label>
                            <Form.Control
                                type="tel"
                                placeholder="+237 612345678"
                                value={whatsappForm.phone_number}
                                onChange={e => setWhatsappForm({ ...whatsappForm, phone_number: e.target.value })}
                                required
                            />
                            <Form.Text className="text-muted">
                                Format: +indicatif pays + numéro (ex: +237 612345678)
                            </Form.Text>
                        </Form.Group>
                        <Form.Group>
                            <Form.Check
                                type="checkbox"
                                label="Définir comme numéro par défaut"
                                checked={whatsappForm.is_default}
                                onChange={e => setWhatsappForm({ ...whatsappForm, is_default: e.target.checked })}
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowWhatsappModal(false)}>Annuler</Button>
                        <Button type="submit" variant="danger">Ajouter</Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Modal Page CMS */}
            <Modal show={showPageModal} onHide={() => setShowPageModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{editingPage ? 'Modifier' : 'Créer'} une page</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handlePageSubmit}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Titre</Form.Label>
                            <Form.Control
                                type="text"
                                value={pageForm.title}
                                onChange={e => setPageForm({ ...pageForm, title: e.target.value })}
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Slug (URL)</Form.Label>
                            <Form.Control
                                type="text"
                                value={pageForm.slug}
                                onChange={e => setPageForm({ ...pageForm, slug: e.target.value.toLowerCase().replace(/ /g, '-') })}
                                required
                            />
                            <Form.Text className="text-muted">
                                Exemple: "a-propos" donnera l'URL /a-propos
                            </Form.Text>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Contenu (HTML)</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={10}
                                value={pageForm.content}
                                onChange={e => setPageForm({ ...pageForm, content: e.target.value })}
                            />
                            <Form.Text className="text-muted">
                                Vous pouvez utiliser du HTML pour formater votre contenu
                            </Form.Text>
                        </Form.Group>
                        <Form.Group>
                            <Form.Check
                                type="checkbox"
                                label="Publiée"
                                checked={pageForm.is_published}
                                onChange={e => setPageForm({ ...pageForm, is_published: e.target.checked })}
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowPageModal(false)}>Annuler</Button>
                        <Button type="submit" variant="danger">Enregistrer</Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </Container>
    );
};

export default SuperAdminDashboard;